/**
 * @fileoverview Shared FeatureTierContext for role, tier, and module-enablement state in ui-admin.
 * @module ui-admin/contexts/FeatureTierContext
 *
 * Responsibilities:
 * - Provide default React context and provider for feature tier, user role, lookup scope, and enabled modules.
 *
 * Dependencies:
 * - react.
 *
 * Architecture:
 * - Shared UI context overridden by host apps (tenant-web, system-web) with local-first Dexie state.
 *
 * Notes:
 * - Default enabled modules use canonical lowerCamelCase module codes.
 */

import React, { createContext, useContext, useState } from 'react';

export type UserRole = 'admin' | 'inspector' | 'viewer';
export type LookupScope = 'system' | 'organization' | 'tenant';

interface FeatureTierState {
  // Legacy (backwards compatibility) - DEVELOPMENT ONLY
  isAdmin: boolean;
  setIsAdmin: (v: boolean) => void;
  isTier2: boolean;
  setIsTier2: (v: boolean) => void;
  hasPermitModule: boolean;
  setHasPermitModule: (v: boolean) => void;
  
  // ⚠️ DEVELOPMENT/TESTING ONLY - Remove before production ⚠️
  // These allow simulating different user roles and module configurations
  // without actual authentication/authorization checks
  userRole: UserRole;
  setUserRole: (role: UserRole) => void;
  tier: number;
  setTier: (tier: number) => void;
  enabledModules: string[];
  setEnabledModules: (modules: string[] | ((prev: string[]) => string[])) => void;
  /** Returns true if the given module key is in enabledModules */
  hasModule: (moduleKey: string) => boolean;

  // AI feature gating
  hasAI: boolean;
  aiCostRails?: { sessionTokenBudget: number };
  aiTokensUsed: number;
  recordAITokens?: (tokens: number) => void;

  // ⚠️ DEVELOPMENT/TESTING ONLY — Lookup scope simulation ⚠️
  // Simulates which admin scope the user is operating in (system/org/tenant)
  // and which value scopes are visible in the lookup panel.
  lookupAdminScope: LookupScope;
  setLookupAdminScope: (scope: LookupScope) => void;
  lookupValueScopeFilter: LookupScope | 'all';
  setLookupValueScopeFilter: (scope: LookupScope | 'all') => void;
}

const FeatureTierContext = createContext<FeatureTierState>({
  isAdmin: false, setIsAdmin: () => {},
  isTier2: true, setIsTier2: () => {},
  hasPermitModule: true, setHasPermitModule: () => {},
  userRole: 'inspector', setUserRole: () => {},
  tier: 2, setTier: () => {},
  enabledModules: ['wildlandFire', 'fireCodeCompliance', 'hydrantManagement', 'customInspectionPrograms', 'billing'], setEnabledModules: () => {}, hasModule: () => true,
  lookupAdminScope: 'tenant', setLookupAdminScope: () => {},
  lookupValueScopeFilter: 'all', setLookupValueScopeFilter: () => {},
  hasAI: false,
  aiCostRails: undefined,
  aiTokensUsed: 0,
  recordAITokens: undefined,
});

export function FeatureTierProvider({ children }: { children: React.ReactNode }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTier2State, setIsTier2State] = useState(true);
  const [hasPermitModule, setHasPermitModule] = useState(true);
  
  // New testing state (defaults unchecked)
  const [userRole, setUserRole] = useState<UserRole>('inspector');
  const [tier, setTierState] = useState(2);
  const [enabledModules, setEnabledModules] = useState<string[]>(['wildlandFire']);
  const hasModule = (moduleKey: string) => enabledModules.includes(moduleKey);
  const hasAI = isTier2State;
  const [aiTokensUsed, setAITokensUsed] = useState(0);
  const recordAITokens = (tokens: number) => setAITokensUsed((prev) => prev + tokens);
  const aiCostRails = { sessionTokenBudget: 50000 };

  // Lookup scope testing state — persisted in localStorage
  const [lookupAdminScope, setLookupAdminScopeState] = useState<LookupScope>(() => {
    try { const v = localStorage.getItem('__test_lookup_admin_scope__'); if (v === 'system' || v === 'organization' || v === 'tenant') return v; } catch {}
    return 'tenant';
  });
  const [lookupValueScopeFilter, setLookupValueScopeFilterState] = useState<LookupScope | 'all'>(() => {
    try { const v = localStorage.getItem('__test_lookup_value_scope_filter__'); if (v === 'all' || v === 'system' || v === 'organization' || v === 'tenant') return v; } catch {}
    return 'all';
  });
  const setLookupAdminScope = (scope: LookupScope) => {
    setLookupAdminScopeState(scope);
    try { localStorage.setItem('__test_lookup_admin_scope__', scope); } catch {}
  };
  const setLookupValueScopeFilter = (scope: LookupScope | 'all') => {
    setLookupValueScopeFilterState(scope);
    try { localStorage.setItem('__test_lookup_value_scope_filter__', scope); } catch {}
  };

  // Keep legacy isTier2 and testing tier value in sync for all consumers.
  const setTier = (nextTier: number) => {
    setTierState(nextTier);
    setIsTier2State(nextTier >= 2);
  };

  const setIsTier2 = (nextIsTier2: boolean) => {
    setIsTier2State(nextIsTier2);
    setTierState(nextIsTier2 ? 2 : 1);
  };

  // Derive isAdmin from userRole so Testing Tools "Administrator" selection propagates everywhere
  const derivedIsAdmin = isAdmin || userRole === 'admin';

  const value: FeatureTierState = {
    isAdmin: derivedIsAdmin, setIsAdmin,
    isTier2: isTier2State, setIsTier2,
    hasPermitModule, setHasPermitModule,
    userRole, setUserRole,
    tier, setTier,
    enabledModules, setEnabledModules, hasModule,
    lookupAdminScope, setLookupAdminScope,
    lookupValueScopeFilter, setLookupValueScopeFilter,
    hasAI,
    aiCostRails,
    aiTokensUsed,
    recordAITokens,
  };

  return (
    <FeatureTierContext.Provider value={value}>
      {children}
    </FeatureTierContext.Provider>
  );
}

export function useFeatureTier() {
  return useContext(FeatureTierContext);
}
